import type {
  BrowserCommand,
  BrowserNavigateCommand,
  BrowserNewTabCommand,
  BrowserSessionSnapshot,
  BrowserSubscribeInput,
  BrowserTabSnapshot,
  DevTunnelRecord,
  EnvironmentId,
  ThreadId,
} from "@cmmd-forge/contracts";

import { isAllowedPreviewUrl, normalizePreviewUrl } from "~/lib/devServerPreviewUrl";
import { isLocalPreviewBrowserUrl } from "./BrowserPreviewPanel.url";

export type BrowserAddressCommand =
  | BrowserNavigateCommand
  | (BrowserNewTabCommand & { readonly url: string });

export interface BrowserCommandRouteVm {
  readonly nativeCommandTabId: string | null;
  readonly shouldKeepServerNavigationRecovery: boolean;
  readonly shouldUseNativeBrowserCommand: boolean;
}

export interface BrowserCommandExecutionPlan {
  readonly commandRoute: BrowserCommandRouteVm;
  readonly serverCommandTimeoutMs: number;
  readonly shouldReportError: boolean;
  readonly visiblePendingType: BrowserCommand["type"] | null;
}

export type BrowserCommandRuntimeAvailability =
  | { readonly type: "available" }
  | {
      readonly type: "unavailable";
      readonly message: string;
    };

export function nativeBrowserCanHandleCommand(command: BrowserCommand): boolean {
  switch (command.type) {
    case "click":
    case "clickPoint":
    case "goBack":
    case "goForward":
    case "navigate":
    case "pressKey":
    case "reload":
    case "type":
    case "typeText":
    case "wheel":
      return true;
    default:
      return false;
  }
}

export function browserCommandTabId(command: BrowserCommand): string | null {
  return "tabId" in command && typeof command.tabId === "string" ? command.tabId : null;
}

export function browserCommandCanChangeActiveAddress(command: BrowserCommand): boolean {
  switch (command.type) {
    case "consoleLogs":
    case "nameSession":
    case "resetViewport":
    case "screenshot":
    case "setViewport":
    case "setVisibility":
    case "snapshot":
      return false;
    default:
      return true;
  }
}

export function browserCommandHasVisiblePendingState(type: BrowserCommand["type"]): boolean {
  switch (type) {
    case "navigate":
    case "newTab":
    case "reload":
    case "screenshot":
      return true;
    default:
      return false;
  }
}

export function browserCommandUsesInteractiveInput(command: BrowserCommand): boolean {
  switch (command.type) {
    case "click":
    case "clickPoint":
    case "pressKey":
    case "scroll":
    case "type":
    case "typeText":
    case "wheel":
      return true;
    default:
      return false;
  }
}

export function browserCommandTimeoutMs(
  command: BrowserCommand,
  options: {
    readonly defaultTimeoutMs?: number;
    readonly interactiveTimeoutMs?: number;
  } = {},
): number {
  if (browserCommandUsesInteractiveInput(command)) {
    return options.interactiveTimeoutMs ?? 12_000;
  }
  return options.defaultTimeoutMs ?? 100_000;
}

export function shouldRefreshBrowserSessionAfterCommand(command: BrowserCommand): boolean {
  return browserCommandUsesInteractiveInput(command);
}

export function resolveBrowserCommandRoute(input: {
  readonly command: BrowserCommand;
  readonly activeTabId: string | null | undefined;
  readonly session: BrowserSessionSnapshot | null;
  readonly nativeBrowserCanCommand: boolean;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
}): BrowserCommandRouteVm {
  const nativeCommandTabId =
    browserCommandTabId(input.command) ?? input.activeTabId ?? input.session?.activeTabId ?? null;
  const shouldKeepServerNavigationRecovery =
    input.command.type === "navigate" &&
    isLocalPreviewBrowserUrl(input.command.url, input.devTunnelRecords);
  return {
    nativeCommandTabId,
    shouldKeepServerNavigationRecovery,
    shouldUseNativeBrowserCommand: Boolean(
      input.nativeBrowserCanCommand &&
      input.session?.browserId &&
      input.session.sessionId &&
      nativeCommandTabId &&
      input.session.display.transport === "native-embedded" &&
      input.session.display.status === "connected" &&
      !shouldKeepServerNavigationRecovery &&
      nativeBrowserCanHandleCommand(input.command),
    ),
  };
}

export function resolveVisiblePendingBrowserCommand(input: {
  readonly commandType: BrowserCommand["type"];
  readonly visiblePending?: boolean | undefined;
}): BrowserCommand["type"] | null {
  if (input.visiblePending === false) {
    return null;
  }
  return browserCommandHasVisiblePendingState(input.commandType) ? input.commandType : null;
}

export function resolveBrowserCommandExecutionPlan(input: {
  readonly command: BrowserCommand;
  readonly activeTabId: string | null | undefined;
  readonly session: BrowserSessionSnapshot | null;
  readonly nativeBrowserCanCommand: boolean;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
  readonly visiblePending?: boolean | undefined;
  readonly reportError?: boolean | undefined;
  readonly timeoutMs?: number | undefined;
  readonly defaultTimeoutMs: number;
}): BrowserCommandExecutionPlan {
  const shouldUseVisiblePending = input.visiblePending !== false;
  return {
    commandRoute: resolveBrowserCommandRoute({
      command: input.command,
      activeTabId: input.activeTabId,
      session: input.session,
      nativeBrowserCanCommand: input.nativeBrowserCanCommand,
      devTunnelRecords: input.devTunnelRecords,
    }),
    serverCommandTimeoutMs:
      input.timeoutMs ??
      browserCommandTimeoutMs(input.command, { defaultTimeoutMs: input.defaultTimeoutMs }),
    shouldReportError: input.reportError !== false,
    visiblePendingType: resolveVisiblePendingBrowserCommand({
      commandType: input.command.type,
      visiblePending: shouldUseVisiblePending,
    }),
  };
}

export function resolveBrowserCommandRuntimeAvailability(input: {
  readonly commandRoute: BrowserCommandRouteVm;
  readonly hasBrowserRuntime: boolean;
}): BrowserCommandRuntimeAvailability {
  if (input.commandRoute.shouldUseNativeBrowserCommand || input.hasBrowserRuntime) {
    return { type: "available" };
  }

  return {
    type: "unavailable",
    message: "Browser runtime is not available for this environment.",
  };
}

export function buildBrowserPreviewSubscriptionInput(input: {
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
}): BrowserSubscribeInput {
  return {
    environmentId: input.environmentId,
    threadId: input.threadId,
  };
}

export function buildBrowserAddressCommand(input: {
  readonly addressValue: string;
  readonly activeTabId: string | null;
  readonly mode: "navigate-active-tab" | "new-tab";
}): BrowserAddressCommand | null {
  if (input.mode === "new-tab" && input.addressValue.trim() === "about:blank") {
    return {
      type: "newTab",
      url: "about:blank",
      makeActive: true,
    };
  }

  const normalized = normalizePreviewUrl(input.addressValue);
  if (!normalized || !isAllowedPreviewUrl(normalized)) {
    return null;
  }

  if (input.mode === "new-tab") {
    return {
      type: "newTab",
      url: normalized,
      makeActive: true,
    };
  }

  if (!input.activeTabId) {
    return {
      type: "newTab",
      url: normalized,
      makeActive: true,
    };
  }

  return {
    type: "navigate",
    tabId: input.activeTabId,
    url: normalized,
  };
}

export function shouldRestartBrowserSessionForAddressRecovery(input: {
  readonly activeTab: BrowserTabSnapshot | null | undefined;
  readonly hasBlockingError: boolean;
  readonly liveDisplayIsAttached: boolean;
}): boolean {
  if (!input.activeTab || input.liveDisplayIsAttached) {
    return false;
  }
  return (
    input.hasBlockingError ||
    input.activeTab.status === "degraded" ||
    Boolean(input.activeTab.lastError)
  );
}
